/**
 * SkuCard — catalog card for a single SKU.
 *
 * Variants:
 *   LIVE  — full-color, gold-gradient border, shows price or "Free".
 *   SOON  — desaturate + opacity via CSS filter, lock glyph, "Soon · {releaseTarget}" badge.
 *   HIDDEN — renders null.
 *
 * Accessibility:
 *   - Rendered as <button type="button"> for native keyboard/pointer semantics.
 *   - aria-label conveys both name and status ("available" / "coming soon").
 *   - Decorative SVGs carry aria-hidden.
 *   - Tap target enforced via min-height/min-width: 44px (WCAG 2.5.5).
 *   - Hover/transform transitions respect prefers-reduced-motion.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Sku } from '../../types/sku';
import { useSkuLabel } from '../../lib/sku-i18n';
import { categoryGlyph } from '../../lib/sku-glyph';
import { exampleUrl, getExamplesFolderForSku } from '../../lib/sku-examples';
import { MoonOrb } from '../brand/MoonOrb';
import styles from './SkuCard.module.css';

/**
 * SKUs that have no R2 example photo but ship a rendered illustration instead
 * (no asset upload / deploy-free image needed). Keyed by SKU code.
 */
const RENDERED_ILLUSTRATION: Readonly<Record<string, () => JSX.Element>> = {
  // Full-moon orb on a deep-space backdrop for "Лунный двойник".
  moon_phase_soulmate: () => <MoonOrb phase={0.5} size={110} />,
};

export interface SkuCardProps {
  sku: Sku;
  onTap?: (sku: Sku) => void;
}

/** Inline SVG lock glyph — crisp at small sizes, fully decorative. */
function LockIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="2"
        y="6"
        width="10"
        height="7"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path
        d="M4.5 6V4.5a2.5 2.5 0 0 1 5 0V6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SkuThumb({ skuCode }: { skuCode: string }): JSX.Element | null {
  const folder = getExamplesFolderForSku(skuCode);
  const [failed, setFailed] = useState(false);
  // No R2 photo (or it 404'd) → use a rendered illustration if one exists.
  if (!folder || failed) {
    const illustration = RENDERED_ILLUSTRATION[skuCode];
    if (illustration) {
      return (
        <div className={styles.thumb}>
          <div className={styles.thumbIllustration}>{illustration()}</div>
        </div>
      );
    }
    return null;
  }
  return (
    <div className={styles.thumb}>
      <img
        src={exampleUrl(folder, 1)}
        alt=""
        className={styles.thumbImg}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

export function SkuCard({ sku, onTap }: SkuCardProps): JSX.Element | null {
  const { t } = useTranslation('flows');
  const skuLabel = useSkuLabel();

  // HIDDEN SKUs are never rendered
  if (sku.status === 'HIDDEN') return null;

  const name = skuLabel(sku.nameKey);
  const desc = skuLabel(sku.descKey);
  const isSoon = sku.status === 'SOON';

  const ariaLabel = isSoon
    ? t('catalog.sku.comingSoonAria', { name })
    : t('catalog.sku.availableAria', { name });

  function handleClick() {
    onTap?.(sku);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTap?.(sku);
    }
  }

  const cardClass = [
    styles.card,
    isSoon ? styles.cardSoon : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={cardClass}
      aria-label={ariaLabel}
      data-sku-state={isSoon ? 'soon' : 'live'}
      data-testid={`catalog-card-${sku.code}`}
      // Inline min-height/min-width so happy-dom tests can read el.style values
      style={{ minHeight: 44, minWidth: 44 }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <SkuThumb skuCode={sku.code} />
      {/* Header: category glyph + name + optional lock icon */}
      <div className={styles.header}>
        <p className={styles.name}>
          <span aria-hidden="true" style={{ marginRight: 6 }}>{categoryGlyph(sku.category)}</span>
          {name}
        </p>
        {isSoon && (
          <span className={styles.lockWrap}>
            <LockIcon />
          </span>
        )}
      </div>

      {/* Description */}
      <p className={styles.desc}>{desc}</p>

      {/* Footer: price or soon badge + category */}
      <div className={styles.footer}>
        {isSoon ? (
          <span className={styles.badge}>
            {sku.releaseTarget ? t('catalog.sku.soonWithTarget', { target: sku.releaseTarget }) : t('catalog.sku.soon')}
          </span>
        ) : (
          <p className={`${styles.price} ${sku.priceStars === null ? styles.priceFree : ''}`}>
            {sku.priceStars === null ? t('catalog.sku.free') : `${sku.priceStars} ⭐`}
          </p>
        )}
        <p className={styles.category}>{sku.category.toLowerCase()}</p>
      </div>
    </button>
  );
}
